import React, { memo } from "react";
import EventItem from "./EventItem";
import * as Tooltip from '@radix-ui/react-tooltip';
import { BookOpen, AlertTriangle } from "lucide-react";

// Modified CalendarCell to include drop functionality
const CalendarCell = memo(({ person, day, userWork, userEvents, userExpirations, qualificationTypes = [], trainings = [], onDayClick, onOrderDoubleClick, onEditTraining, viewType = 'users' }) => {
  const isToday = day.toDateString() === new Date().toDateString();

  const dayUserWork = userWork.filter((item) => {
    if (!item.planned_date_start || !item.userId) return false;
    const itemDate = new Date(item.planned_date_start);
    return itemDate.toDateString() === day.toDateString() && item.userId === person.username;
  });

  const dayUserEvents = viewType === 'users' 
    ? userEvents.filter((item) => {
        if ((!item.start_date && !item.planned_date_start) || !item.userId) return false;
        const itemDate = new Date(item.start_date || item.planned_date_start);
        return itemDate.toDateString() === day.toDateString() && item.userId === person.id;
      })
    : // In trainings view, show training information on the specific training days
      (() => {
        if (!person.start_date || !person.end_date) {
          console.log('Missing training dates for person:', person);
          return [];
        }
        
        const trainingStart = new Date(person.start_date);
        const trainingEnd = new Date(person.end_date);
        
        // Reset time components for accurate date comparison
        trainingStart.setHours(0, 0, 0, 0);
        trainingEnd.setHours(23, 59, 59, 999);
        const currentDay = new Date(day);
        currentDay.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
        
        // Check if this day is within the training period
        if (currentDay.getTime() >= trainingStart.getTime() && currentDay.getTime() <= trainingEnd.getTime()) {
          // Return training information as an event for this day
          return [{
            id: `training-info-${person.id}-${day.toDateString()}`,
            type: 'training',
            trainingId: person.id,
            trainingName: person.name || person.qualification_name || 'Training',
            description: `${person.name || person.qualification_name || 'Training'} - ${person.participants_count || 0} participants`,
            start_date: person.start_date,
            end_date: person.end_date,
            participants_count: person.participants_count || 0,
            status: person.status || 'Active',
            training_center_name: person.training_center_name || 'Training Center'
          }];
        }
        return [];
      })();

  const dayUserExpirations = userExpirations.filter((item) => {
    if (!item.expiryDate || !item.userId) return false;
    const itemDate = new Date(item.start_date || item.expiryDate);
    return itemDate.toDateString() === day.toDateString() && item.userId === person.username;
  });

  const allItems = [...dayUserEvents, ...dayUserExpirations];
  const hasItems = allItems.filter(item => item.type !== "travel").length > 0;

  const trainingItems = allItems.filter(item => item.type === "training");
  const qualificationAlertItems = allItems.filter(item => item.type === "qualificationalert");
  const trainingCount = trainingItems.length;
  const qualificationAlertCount = qualificationAlertItems.length;

  const dropClass = '';

  const getCellHeight = () => {
    // Fixed height for optimized rendering
    return "h-[50px] max-h-[50px]";
  };

  // Component for grouped events with tooltip
  const GroupedEventItem = ({ items, type, title, bgClass, borderColor, icon: Icon, trainings = [] }) => {
    let tooltipTitle = title;
    let tooltipContent = '';

    if (type === 'training') {
      tooltipTitle = `More than one training`;
      tooltipContent = items.map(item => {
        const trainingName = item.trainingName || item.description || 'Unnamed training';
        const qualificationName = item.qualificationName || 'Unknown qualification';
        
        // Try to find company training number from qualificationTypes
        let companyTrainingNumber = null;
        if (qualificationTypes && qualificationTypes.length > 0) {
          const qualType = qualificationTypes.find(qt => 
            qt.name === qualificationName && qt.company_data
          );
          if (qualType && qualType.company_data && qualType.company_data.training_number) {
            companyTrainingNumber = qualType.company_data.training_number;
          }
        }
        
        // Get participants info from original training data
        let participantsInfo = '';
        
        if (item.originalTrainingId && trainings.length > 0) {
          const originalTraining = trainings.find(t => t.id === item.originalTrainingId);
          
          if (originalTraining) {
            const participantsCount = originalTraining.participants_count || (originalTraining.users_data ? originalTraining.users_data.length : 0);
            const maxParticipants = originalTraining.availability || originalTraining.training_center_qualification_data?.availability;
            
            
            if (maxParticipants) {
              participantsInfo = `\n    Participants: ${participantsCount}/${maxParticipants}`;
            } else {
              participantsInfo = `\n    Participants: ${participantsCount}`;
            }
            
          }
        }
        
        const status = item.status || 'planned';
        
        let trainingInfo = `• ${trainingName}\n    Qualification: ${qualificationName}`;
        if (companyTrainingNumber) {
          trainingInfo += `\n    Training No: ${companyTrainingNumber}`;
        }
        trainingInfo += participantsInfo;
        trainingInfo += `\n    Status: ${status}`;
        
        return trainingInfo;
      }).join('\n\n');
    } else if (type === 'qualification') {
      // Since all qualifications expire on the same day, use the first item's daysRemaining
      const firstItem = items[0];
      const daysText = firstItem.daysRemaining < 0 
        ? `expired ${Math.abs(firstItem.daysRemaining)} days ago`
        : `expires in ${firstItem.daysRemaining} days`;
      
      tooltipTitle = `More than one qualification ${daysText}`;
      tooltipContent = items.map(item => 
        `• ${item.trainingName || 'Qualification'}`
      ).join('\n');
    } else {
      tooltipContent = items.map(item => 
        `• ${item.description || 'Event'}`
      ).join('\n');
    }

    return (
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <div
            onDoubleClick={(e) => {
              e.stopPropagation();
              // For grouped items, trigger the first item's action
              if (items.length > 0) {
                const firstItem = items[0];
                if (type === 'qualification') {
                  onOrderDoubleClick(person, day, firstItem, { openQualification: true, qualificationId: firstItem.qualificationId });
                } else {
                  onOrderDoubleClick(person, day, firstItem);
                }
              }
            }}
            className={`p-1 rounded text-[9px] border shadow-sm ${bgClass} hover:shadow-md transition-shadow relative overflow-hidden cursor-pointer`}
          >
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${borderColor}`}></div>
            <div className="pl-1">
              <div className="font-medium text-[9px] mb-0.5 flex items-center justify-between">
                <div className="flex items-center overflow-hidden">
                  <Icon className="h-2 w-2 mr-0.5 flex-shrink-0 text-slate-600" />
                  <span className="truncate">{title}</span>
                </div>
                <span className="text-[8px] bg-slate-600 text-white rounded-full px-1 py-0 ml-1">
                  {items.length}
                </span>
              </div>
              <div className="text-[8px] text-slate-500 truncate">
                {type === 'training' ? 'Click to see details' : 'Multiple items expiring'}
              </div>
            </div>
          </div>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className="bg-slate-800 text-white text-xs rounded-md p-3 shadow-lg max-w-md z-50"
            side="right"
            sideOffset={5}
          >
            <div className="whitespace-pre">
              <strong>{tooltipTitle}</strong>
              {tooltipContent && (
                <>
                  {'\n\n'}{tooltipContent}
                </>
              )}
            </div>
            <Tooltip.Arrow className="fill-slate-800" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    );
  };

  return (
    <div
      className={`border-t p-1 ${getCellHeight()} overflow-hidden box-border ${hasItems ? (day.getDay() === 0 ? "bg-red-100" : "bg-brand-100") : day.getDay() === 0 ? "bg-red-50" : isToday ? "bg-green-50" : "bg-white"} transition-colors`}
      onClick={() => onDayClick(person, day)}
    >
      {allItems.length > 0 ? (
        <div className="space-y-1 mt-1">
          {/* If only 1 event total - show it in full */}
          {(trainingCount + qualificationAlertCount) === 1 ? (
            <>
              {trainingCount === 1 && trainingItems.map((item) => (
                <EventItem
                  key={item.id}
                  item={item}
                  userId={viewType === 'users' ? person.username : person.id}
                  date={day}
                  onDoubleClick={(item, options) => onOrderDoubleClick(person, day, item, options)}
                  onEditTraining={(trainingItem) => onEditTraining(person, day, trainingItem)}
                  qualificationTypes={qualificationTypes}
                  trainings={trainings}
                />
              ))}
              {qualificationAlertCount === 1 && qualificationAlertItems.map((item) => (
                <EventItem
                  key={item.id}
                  item={item}
                  userId={viewType === 'users' ? person.username : person.id}
                  date={day}
                  onDoubleClick={(item, options) => onOrderDoubleClick(person, day, item, options)}
                  onEditTraining={(trainingItem) => onEditTraining(person, day, trainingItem)}
                  qualificationTypes={qualificationTypes}
                  trainings={trainings}
                />
              ))}
            </>
          ) : (
            /* If 2+ events - show summary with tooltip */
            (() => {
              // Determine which type of events we have
              const onlyTrainings = trainingCount > 0 && qualificationAlertCount === 0;
              const onlyQualifications = trainingCount === 0 && qualificationAlertCount > 0;
              const bothTypes = trainingCount > 0 && qualificationAlertCount > 0;

              // For qualification-only, determine color based on most critical
              let qualBgClass = "bg-steel-100";
              let qualBorderClass = "border-steel-300";
              let qualTextClass = "text-slate-700";

              if (onlyQualifications) {
                const mostCritical = qualificationAlertItems.reduce((prev, current) =>
                  (current.daysRemaining < prev.daysRemaining) ? current : prev
                );
                const days = mostCritical.daysRemaining;

                if (days < 0) {
                  qualBgClass = "bg-red-200";
                  qualBorderClass = "border-red-400";
                  qualTextClass = "text-red-700";
                } else if (days <= 30) {
                  qualBgClass = "bg-orange-100";
                  qualBorderClass = "border-orange-400";
                  qualTextClass = "text-orange-700";
                } else if (days <= 60) {
                  qualBgClass = "bg-yellow-100";
                  qualBorderClass = "border-yellow-400";
                  qualTextClass = "text-yellow-700";
                }
              }

              // Determine left border color
              let leftBorderColor = "bg-slate-500";
              if (onlyTrainings) {
                leftBorderColor = "bg-brand-500";
              } else if (onlyQualifications) {
                const mostCritical = qualificationAlertItems.reduce((prev, current) =>
                  (current.daysRemaining < prev.daysRemaining) ? current : prev
                );
                const days = mostCritical.daysRemaining;
                if (days < 0) {
                  leftBorderColor = "bg-red-600";
                } else if (days <= 30) {
                  leftBorderColor = "bg-orange-600";
                } else if (days <= 60) {
                  leftBorderColor = "bg-yellow-600";
                }
              }

              return (
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <div className={`p-1 rounded text-[9px] border shadow-sm cursor-pointer transition-colors relative overflow-hidden ${
                      onlyTrainings ? 'bg-brand-200 border-brand-400 hover:bg-brand-300' :
                      onlyQualifications ? `${qualBgClass} ${qualBorderClass} hover:opacity-80` :
                      'bg-steel-100 border-steel-300 hover:bg-steel-200'
                    }`}>
                      {/* Left border bar */}
                      <div className={`absolute left-0 top-0 bottom-0 w-1 ${leftBorderColor}`}></div>

                      <div className="pl-1">
                        <div className={`font-medium text-[9px] mb-0.5 ${
                          onlyTrainings ? 'text-brand-600' :
                          onlyQualifications ? qualTextClass :
                          'text-slate-700'
                        }`}>
                          {onlyTrainings && (
                            <div className="flex items-center">
                              <BookOpen className="h-2 w-2 mr-0.5" />
                              <span className="truncate">{trainingCount} training{trainingCount > 1 ? 's' : ''}</span>
                            </div>
                          )}
                          {onlyQualifications && (
                            <div className="flex items-center">
                              <AlertTriangle className="h-2 w-2 mr-0.5" />
                              <span className="truncate">{qualificationAlertCount} qualification{qualificationAlertCount > 1 ? 's' : ''}</span>
                            </div>
                          )}
                          {bothTypes && (
                            <>
                              <div className="flex items-center">
                                <BookOpen className="h-2 w-2 mr-0.5 text-brand-500" />
                                <span className="truncate">{trainingCount} training{trainingCount > 1 ? 's' : ''}</span>
                              </div>
                              <div className="flex items-center">
                                <AlertTriangle className="h-2 w-2 mr-0.5 text-orange-600" />
                                <span className="truncate">{qualificationAlertCount} qualification{qualificationAlertCount > 1 ? 's' : ''}</span>
                              </div>
                            </>
                          )}
                        </div>
                        {/* Second line - empty placeholder to match single event height */}
                        {!bothTypes && (
                          <div className="text-[8px] text-slate-500 truncate opacity-0">
                            placeholder
                          </div>
                        )}
                      </div>
                    </div>
                  </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  className="bg-slate-800 text-white text-xs rounded-md p-3 shadow-lg max-w-md z-50"
                  side="right"
                  sideOffset={5}
                >
                  <div className="space-y-2">
                    {trainingCount > 0 && (
                      <div>
                        <div className="font-semibold text-brand-300 mb-1">Trainings:</div>
                        {trainingItems.map((item, idx) => (
                          <div key={idx} className="text-[11px] ml-2">
                            • {item.trainingName || item.description || 'Training'}
                            {item.qualificationName && (
                              <span className="text-slate-400"> - {item.qualificationName}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {qualificationAlertCount > 0 && (
                      <div>
                        <div className="font-semibold text-orange-300 mb-1">Ending Qualifications:</div>
                        {qualificationAlertItems.map((item, idx) => (
                          <div key={idx} className="text-[11px] ml-2">
                            • {item.trainingName || 'Qualification'}
                            <span className={`ml-1 ${
                              item.daysRemaining < 0 ? 'text-red-400' :
                              item.daysRemaining <= 30 ? 'text-orange-400' :
                              'text-yellow-400'
                            }`}>
                              ({item.daysRemaining < 0
                                ? `expired ${Math.abs(item.daysRemaining)} days ago`
                                : `${item.daysRemaining} days left`})
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Tooltip.Arrow className="fill-slate-800" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
              );
            })()
          )}
        </div>
      ) : (
        <div className="flex items-center justify-center h-full">
          <span className="text-[9px] text-slate-400">No events</span>
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for better memoization
  return (
    prevProps.person.id === nextProps.person.id &&
    prevProps.day.getTime() === nextProps.day.getTime() &&
    prevProps.viewType === nextProps.viewType &&
    prevProps.userEvents === nextProps.userEvents &&
    prevProps.userExpirations === nextProps.userExpirations &&
    prevProps.trainings === nextProps.trainings &&
    prevProps.userWork === nextProps.userWork
  );
});

export default CalendarCell;